import * as THREE from "three";
import TWEEN from "@tweenjs/tween.js";
import TexturedGeoMesh from "./TexturedGeoMesh";
import CylinderSeam from "./CylinderSeam";

/**
 * Creates a photosphere with the given image
 * @example
 * let photoSphere = new PhotoCylinder(image);
 * scene.add(photoSphere);
 */
export default class PhotoCylinder extends TexturedGeoMesh {
  raycaster;
  zoomEvent;
  canLockPolar;
  limitAxisTimeout;
  currentPolarLimit;
  allowPolarLimit;
  imageFov;

  /**
   * Creates a photosphere based on the given URL
   *
   * @param player
   * @param {object} image - image object containing full and optional low url
   * @param loadedCallback
   * @param promiseCallback
   * @param stereo
   * @param lowRes
   */
  constructor(
    player,
    image,
    loadedCallback = () => {},
    promiseCallback = () => {},
    stereo,
    lowRes = true,
  ) {
    // cylinder starting bounds
    const height = 800;
    const radius = 600;

    const geometry = new THREE.CylinderBufferGeometry(
      radius,
      radius,
      height,
      64,
      1,
      true,
    );

    // create the mesh with the projected material
    super(
      player,
      geometry,
      "basic",
      texture => {
        // on texture loaded event
        this.scaleMesh(texture.image.width, texture.image.height); // fit mesh to image aspect
      },
      180,
    );

    this.imageFov = image.fov ?? null;
    this.raycaster = new THREE.Raycaster();

    this.allowPolarLimit = true;
    this.material.side = THREE.DoubleSide;
    this.image = image;
    this.stereo = stereo;
    this.flipTexture = true;
    this.canLockPolar = false;
    this.player = player;

    let prom = this.loadTexture(
      image,
      this.flipTexture,
      loadedCallback,
      stereo,
      lowRes,
    );
    promiseCallback(
      prom.then(() => {
        // setup cylinder specific controls
        this.setupControls();
        this.canLockPolar = true;

        this._panoSeam = this.createPanoSeam();
        this.add(this._panoSeam);
      }),
    );
  }

  beforeTransition() {
    this.canLockPolar = false;
  }

  afterTransition() {
    this.limitAxis(true, 1);
    this.canLockPolar = true;
  }

  addListeners() {
    this.zoomEvent = () => {
      if (this.canLockPolar) {
        this.limitAxis(false);
      }
    };

    this.player.renderer.domElement.addEventListener(
      "wheel",
      this.zoomEvent,
      false,
    );
    this.player.renderer.domElement.addEventListener(
      "touchmove",
      this.zoomEvent,
      false,
    );
    this.player.renderer.domElement.addEventListener(
      "mousemove",
      this.zoomEvent,
      false,
    );
    this.player.renderer.domElement.addEventListener(
      "keydown",
      this.zoomEvent,
      false,
    );
    window.addEventListener("resize", this.zoomEvent, false);
  }

  removeListeners() {
    this.player.renderer.domElement.removeEventListener(
      "wheel",
      this.zoomEvent,
    );
    this.player.renderer.domElement.removeEventListener(
      "touchmove",
      this.zoomEvent,
    );
    this.player.renderer.domElement.removeEventListener(
      "mousemove",
      this.zoomEvent,
    );
    this.player.renderer.domElement.removeEventListener(
      "keydown",
      this.zoomEvent,
    );
    window.removeEventListener("resize", this.zoomEvent);
  }

  /**
   *
   * @param animate
   * @param timeout
   * @param after
   */
  limitAxis(animate, timeout = 1, after = null) {
    if (this.limitAxisTimeout) {
      clearTimeout(this.limitAxisTimeout);
    }
    this.limitAxisTimeout = setTimeout(() => {
      this.limitPolar(animate, after);
    }, timeout);
  }

  calcTargetPolarLimit() {
    // console.log("calcTargetPolarLimit");
    let dimensions = this.getRealDimensions();
    let half = dimensions.height / 2;
    let hDistance = this.getForwardDistance();
    let upHypoAngle = Math.atan(half / hDistance);

    let alpha = this.player.camera.fov / 2 / this.player.camera.zoom;
    let offset = Math.atan(0.2 * this.player.camera.zoom);

    let upperViewAngle = 90 + upHypoAngle * (180 / Math.PI) - alpha - offset;
    let lowerViewAngle = 90 - upHypoAngle * (180 / Math.PI) + alpha + offset;

    return {
      maxPolarAngle: THREE.MathUtils.degToRad(upperViewAngle),
      minPolarAngle: THREE.MathUtils.degToRad(lowerViewAngle),
    };
  }

  /**
   * limit vertical view angle - hides the top/bottom of the cylinder from the field of view
   *
   * @param animate
   * @param after
   */
  limitPolar(animate = false, after = null) {
    let player = this.player;
    let cylinder = this;

    if (!player.sceneManager.containsHolder(cylinder)) {
      // ignore if not active scene
      return;
    }

    if (player) {
      let camera = player.camera;

      if (camera) {
        if (this.allowPolarLimit) {
          let targetPolarLimit = this.calcTargetPolarLimit();

          if (animate) {
            this.currentPolarLimit = {
              maxPolarAngle: player.controlsOrbit.maxPolarAngle,
              minPolarAngle: player.controlsOrbit.minPolarAngle,
            };

            this.allowPolarLimit = false;
            new TWEEN.Tween(this.currentPolarLimit)
              .to(targetPolarLimit, 500)
              .easing(TWEEN.Easing.Quadratic.In)
              .onUpdate(() => {
                player.controlsOrbit.maxPolarAngle =
                  this.currentPolarLimit.maxPolarAngle;
                player.controlsOrbit.minPolarAngle =
                  this.currentPolarLimit.minPolarAngle;
              })
              .onComplete(() => {
                this.allowPolarLimit = true;
                if (after) {
                  after();
                }
              })
              .start();
          } else {
            player.controlsOrbit.maxPolarAngle = targetPolarLimit.maxPolarAngle;
            player.controlsOrbit.minPolarAngle = targetPolarLimit.minPolarAngle;
            if (after) {
              after();
            }
          }
        }
      }
    }
  }

  /**
   *
   * @returns {CylinderSeam}
   */
  createPanoSeam() {
    let panoSeam;

    let dimensions = this.getRealDimensions();
    console.log(dimensions);

    panoSeam = new CylinderSeam(this.player, 0, false, 1, 800);
    panoSeam.rotation.z = 0;
    panoSeam.position.z = 590;
    panoSeam.position.x = -1;
    return panoSeam;
  }

  /**
   *
   * @returns {null|number}
   */
  getForwardDistance() {
    this.updateCameraCoords();
    let direction = new THREE.Vector3();
    direction.copy(this.cameraDirection);
    direction.setY(0);
    return this.getFaceDistanceFromCamera(direction, false);
  }

  /**
   *
   * @param width
   * @param height
   */
  scaleMesh(width, height) {
    let scale = this.getGeometryDimensions().aspect / (width / height);
    this.scale.set(1.0, scale, 1.0); // scale cylinder height
  }

  /**
   *
   * @param direction
   * @param updateCoords
   * @returns {null|number}
   */
  getFaceDistanceFromCamera(direction, updateCoords = true) {
    if (updateCoords) {
      this.updateCameraCoords();
    }
    this.raycaster.setFromCamera(direction, this.player.camera);
    let intersects = this.raycaster.intersectObject(this);

    let face = intersects[0] ?? null;
    if (face) {
      return face.distance;
    }

    return null;
  }

  setupControls() {
    if (this.player) {
      this.player.controlsDefault();
      this.player.resetControls(false);
      // this.player.controlsOrbit.minZoom = 1;
    }
  }

  getHotspotOptions(is3D = false) {
    if (this.imageFov) {
      return {
        distance: 500,
        scale: this.imageFov / 80,
      };
    }

    return {
      distance: 500,
      scale: 0.5,
    };
  }

  dispose() {
    super.dispose();
    if (this.limitAxisTimeout) {
      clearTimeout(this.limitAxisTimeout);
      this.limitAxisTimeout = undefined;
    }
    this.raycaster = undefined;
    this.currentPolarLimit = undefined;
    this.zoomEvent = undefined;
    this.canLockPolar = false;
    this.allowPolarLimit = false;
  }
}
